import React from 'react';
import { PieChart, Building2, CreditCard } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

interface CostItem {
    label: string;
    value: string;
}

interface CostAnalysisProps {
    systems: any[];
    loading: boolean;
}

const CostAnalysis: React.FC<CostAnalysisProps> = ({ systems, loading }) => {
    // Aggregation Logic
    const aggregateBy = (field: string) => {
        const map = new Map<string, number>();
        systems.forEach(system => {
            const label = system[field] || 'Unassigned';
            const value = parseFloat(system.monthlyCost) || 0;
            map.set(label, (map.get(label) || 0) + value);
        });
        return Array.from(map.entries())
            .map(([label, value]) => ({ 
                label, 
                value: `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                numeric: value
            }))
            .sort((a, b) => b.numeric - a.numeric);
    };

    const categoryCosts = aggregateBy('category');
    const departmentCosts = aggregateBy('department');
    const paymentMethodCosts = aggregateBy('paymentMethod');

    const CostCard = ({ title, icon, items }: { title: string, icon: React.ReactNode, items: CostItem[] }) => (
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex-1 min-w-[300px]">
            <div className="flex items-center gap-2 mb-6">
                <div className="text-gray-900">{icon}</div>
                <h4 className="font-bold text-gray-800">{title}</h4>
            </div>
            <div className="space-y-4">
                {items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center group">
                        <span className="text-sm font-medium text-gray-600 transition-colors group-hover:text-blue-600">
                            {item.label}
                        </span>
                        <Badge variant="outline" className="rounded-lg bg-gray-50 text-gray-700 border-gray-200 font-bold text-sm px-3 py-1">
                            {item.value}
                        </Badge>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {loading ? (
                <div className="p-20 flex justify-center items-center h-[300px] bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                </div>
            ) : systems.length === 0 ? (
                <div className="p-20 text-center text-gray-500 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    No cost data available.
                </div>
            ) : (
                <div className="flex flex-col lg:flex-row gap-6">
                    <CostCard
                        title="Cost by Category"
                        icon={<PieChart size={20} />}
                        items={categoryCosts}
                    />
                    <CostCard
                        title="Cost by Department"
                        icon={<Building2 size={20} />}
                        items={departmentCosts}
                    />
                    <CostCard
                        title="Cost by Payment Method"
                        icon={<CreditCard size={20} />}
                        items={paymentMethodCosts}
                    />
                </div>
            )}
        </div>
    );
};

export default CostAnalysis;
